import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';
import { internalAuditAddUrl } from '@/utils/urls';

import type { useGetCollectionTableProps } from './config';
import { buildInternalAuditRegisterToolbarConfig } from './registerToolbarConfig';
import type { InternalAuditRegisterFields } from './types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `(12)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  getActiveRibbonFilters: () => FilterModal[];
}

/**
 * Object toolbar for the Internal Audit register, mounted only when the
 * `object_toolbar` flag is on. Owning `useExportOptions` here (rather than on the
 * page) keeps it off the legacy `ExportButton` path — with the flag off this
 * component never renders, so the export options are computed once, by whichever
 * path is actually shown (CUS-165, mirroring the Actions/Risks registers).
 */
export const InternalAuditRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'internalAudits' });
  const navigate = useNavigate();
  const [showPdfModal, setShowPdfModal] = useState(false);
  // Ribbon "Edit Filters" opener bridged up from CustomisableRibbon (relocate
  // mode). Present only when the user can manage the ribbon; drives the toolbar's
  // overflow "Edit" group.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<InternalAuditRegisterFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig = useMemo(
    () =>
      buildInternalAuditRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        create: {
          label: st('create_new_button'),
          onClick: () => navigate(internalAuditAddUrl()),
        },
        editRibbon: ribbonConfig
          ? {
              groupLabel: t('edit'),
              label: t('objectToolbar.filterSettings'),
              onClick: ribbonConfig.openEditRibbon,
            }
          : undefined,
      }),
    [title, counter, exportOptions, ribbonConfig, t, st, navigate]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
